import { NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { IMemberPolicy, MemberPolicy } from "../contracts/policies/member-policy";
import { MemberPolicyResource } from "../models/policies/member-policy-resource";

export class MemberSecurityMapper {
    /**
     * Map policy resources to policies.
     * @param client - An instance of an extended Content Hub client
     * @param policyResources - A collection of resources to map
     * @returns An array of policies.
     */
    static async mapToMemberPoliciesAsync(
        client: IExtendedContentHubClient,
        policyResources: Array<MemberPolicyResource>
    ): Promise<Array<IMemberPolicy>> {
        if (policyResources == null || policyResources.length === 0) {
            return [];
        }

        const list: Array<IMemberPolicy> = [];
        for (const memberPolicyResource of policyResources) {
            const memberPolicy = await this.mapToMemberPolicyAsync(client, memberPolicyResource);
            if (memberPolicy != null) {
                list.push(memberPolicy);
            }
        }

        return list;
    }

    /**
     * Map a policy resource to a member policy instance.
     * @param client - An instance of an extended Content Hub client
     * @param policyResource - A policy resource
     * @returns A member policy or null.
     */
    static async mapToMemberPolicyAsync(
        client: IExtendedContentHubClient,
        policyResource: MemberPolicyResource
    ): NullableResultPromise<IMemberPolicy> {
        if (policyResource == null) {
            return null;
        }

        const definitionName = await client.linkHelper.nameFromDefinitionAsync(policyResource.definition);

        const memberPolicy: MemberPolicy = new MemberPolicy({
            canRead: policyResource.canRead,
            canWrite: policyResource.canWrite,
            definitionName: definitionName!,
            target: policyResource.target,
        });

        return memberPolicy;
    }

    /**
     * Map member policy instances to policy resources.
     * @param client - An instance of an extended Content Hub client
     * @param memberPolicies - A collection of member policies
     * @returns An array of policy resources.
     */
    static async mapToMemberPolicyResourcesAsync(
        client: IExtendedContentHubClient,
        memberPolicies: Array<IMemberPolicy>
    ): Promise<Array<MemberPolicyResource>> {
        if (memberPolicies == null || memberPolicies.length === 0) {
            return [];
        }

        const list: Array<MemberPolicyResource> = [];
        for (const memberPolicy of memberPolicies) {
            const policyResource = await this.mapToMemberPolicyResourceAsync(client, memberPolicy);
            if (policyResource != null) {
                list.push(policyResource);
            }
        }

        return list;
    }

    /**
     * Map a member policy to a policy resource.
     * @param client - An instance of an extended Content Hub client
     * @param memberPolicy - A member policy instance
     * @returns A policy resource or null.
     */
    static async mapToMemberPolicyResourceAsync(
        client: IExtendedContentHubClient,
        memberPolicy: IMemberPolicy
    ): NullableResultPromise<MemberPolicyResource> {
        if (memberPolicy == null) {
            return null;
        }

        const definitionLink = await client.linkHelper.definitionToLinkAsync(memberPolicy.definitionName);

        const memberPolicyResource: MemberPolicyResource = new MemberPolicyResource({
            canRead: memberPolicy.canRead,
            canWrite: memberPolicy.canWrite,
            definition: definitionLink,
            target: memberPolicy.target,
        });

        return memberPolicyResource;
    }
}
